// src/components/BookingCalendar.jsx
import React, { useState, useEffect, useRef } from 'react';
import { Calendar } from 'primereact/calendar';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Toast } from 'primereact/toast';
import axios from 'axios';
import 'primeicons/primeicons.css';

import useAuth from "hooks/useAuth";

const BookingCalendar = () => {
    const [bookings, setBookings] = useState([]);
    const [filteredBookings, setFilteredBookings] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedBooking, setSelectedBooking] = useState(null);
    const [dialogVisible, setDialogVisible] = useState(false);
    const [dateRange, setDateRange] = useState(null);

    const toast = useRef(null);
    const auth = useAuth();

    const API_URL = process.env.REACT_APP_API_PATH;

    // ✅ FETCH BOOKINGS
    useEffect(() => {
        if (!auth?.accessToken) return;

        const source = axios.CancelToken.source();

        const fetchBookings = async () => {
            setLoading(true);
            try {
                const response = await axios.get(`${API_URL}partner/calendar`, {
                    headers: {
                        Authorization: `Bearer ${auth.accessToken}`
                    },
                    params: {
                        startDate: '2024-01-01',
                        endDate: '2026-12-31'
                    },
                    cancelToken: source.token
                });

                setBookings(response.data || []);
                setFilteredBookings(response.data || []);
            } catch (error) {
                if (axios.isCancel(error)) return;

                console.error('API Error:', error);

                toast.current?.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Failed to load bookings',
                    life: 3000
                });
            } finally {
                setLoading(false);
            }
        };

        fetchBookings();

        return () => {
            source.cancel("Component unmounted");
        };

    }, [auth?.accessToken]);

    // ✅ FILTER BY DATE
    const handleDateFilter = (e) => {
        const value = e.value;
        setDateRange(value);

        if (!value || value.length !== 2) {
            setFilteredBookings(bookings);
            return;
        }

        const [startDate, endDate] = value;

        const filtered = bookings.filter(booking => {
            const pickupDate = new Date(booking.pickupDate);
            return pickupDate >= startDate && pickupDate <= endDate;
        });

        setFilteredBookings(filtered);
    };

    // ✅ TAG HELPERS
    const getStatusTag = (status) => {
        const severity = {
            'Pending': 'warning',
            'Confirmed': 'success',
            'In Progress': 'info',
            'Completed': 'secondary',
            'Cancelled': 'danger'
        };
        return <Tag value={status} severity={severity[status] || 'secondary'} />;
    };

    const getPaymentStatusTag = (paymentStatus) => {
        const severity = {
            'Paid': 'success',
            'Pending': 'warning',
            'Failed': 'danger'
        };
        return <Tag value={paymentStatus} severity={severity[paymentStatus] || 'secondary'} />;
    };

    // ✅ FORMATTERS
    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD'
        }).format(amount || 0);
    };

    const formatDate = (date) => {
        return new Date(date).toLocaleString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // ✅ TIMELINE VIEW
    const timelineView = () => {
        const bookingsByDate = {};

        filteredBookings.forEach(booking => {
            const pickupDate = new Date(booking.pickupDate).toDateString();
            const deliveryDate = new Date(booking.deliveryDate).toDateString();

            if (!bookingsByDate[pickupDate]) {
                bookingsByDate[pickupDate] = { pickup: [], delivery: [] };
            }
            if (!bookingsByDate[deliveryDate]) {
                bookingsByDate[deliveryDate] = { pickup: [], delivery: [] };
            }

            bookingsByDate[pickupDate].pickup.push(booking);

            if (pickupDate !== deliveryDate) {
                bookingsByDate[deliveryDate].delivery.push(booking);
            }
        });

        const sortedDates = Object.keys(bookingsByDate)
            .sort((a, b) => new Date(a) - new Date(b));

        return (
            <div className="timeline-view">
                {sortedDates.map(date => (
                    <div key={date} className="timeline-row">
                        <h4>{new Date(date).toDateString()}</h4>

                        <div className="timeline-section">
                            <strong>Pickups</strong>
                            {bookingsByDate[date].pickup.length === 0 && <p>No pickups</p>}

                            {bookingsByDate[date].pickup.map(booking => (
                                <div key={booking.id} onClick={() => {
                                    setSelectedBooking(booking);
                                    setDialogVisible(true);
                                }}>
                                    {booking.bookingReference} - {getStatusTag(booking.status)}
                                </div>
                            ))}
                        </div>

                        <div className="timeline-section">
                            <strong>Deliveries</strong>
                            {bookingsByDate[date].delivery.length === 0 && <p>No deliveries</p>}

                            {bookingsByDate[date].delivery.map(booking => (
                                <div key={booking.id} onClick={() => {
                                    setSelectedBooking(booking);
                                    setDialogVisible(true);
                                }}>
                                    {booking.bookingReference} - {getStatusTag(booking.status)}
                                </div>
                            ))}
                        </div>
                    </div>
                ))}
            </div>
        );
    };

    // ✅ LOADING
    if (loading) {
        return (
            <div className="loading-container">
                <ProgressSpinner />
            </div>
        );
    }

    return (
        <div className="booking-calendar-container">
            <Toast ref={toast} />

            <div className="header">
                <h2>Booking Timeline</h2>

                <Calendar
                    value={dateRange}
                    onChange={handleDateFilter}
                    selectionMode="range"
                    readOnlyInput
                    showIcon
                    placeholder="Filter by date"
                />

                {dateRange && (
                    <Button
                        icon="pi pi-times"
                        onClick={() => {
                            setDateRange(null);
                            setFilteredBookings(bookings);
                        }}
                    />
                )}
            </div>

            {filteredBookings.length === 0
                ? <p>No bookings found</p>
                : timelineView()
            }

            <Dialog
                header="Booking Details"
                visible={dialogVisible}
                style={{ width: '40vw' }}
                onHide={() => setDialogVisible(false)}
            >
                {selectedBooking && (
                    <div>
                        <p><b>Ref:</b> {selectedBooking.bookingReference}</p>
                        <p><b>Status:</b> {getStatusTag(selectedBooking.status)}</p>
                        <p><b>Payment:</b> {getPaymentStatusTag(selectedBooking.paymentStatus)}</p>
                        <p><b>Pickup:</b> {formatDate(selectedBooking.pickupDate)}</p>
                        <p><b>Delivery:</b> {formatDate(selectedBooking.deliveryDate)}</p>
                        <p><b>Amount:</b> {formatCurrency(selectedBooking.totalAmount)}</p>
                    </div>
                )}
            </Dialog>
        </div>
    );
};

export default BookingCalendar;